import NiceModal from "@ebay/nice-modal-react"
import { ArrowPathIcon } from "@heroicons/react/24/solid"
import React, { useEffect, useState } from "react"
import { useMutation, useQuery } from "urql"

import { PAYMENT_STATUS_OPTIONS } from "../../../../@core/appointment/appointment.model"
import { useManualBooking } from "../../../../contexts/ManualBookingContext"
import { usePractice } from "../../../../contexts/PracticeContext"
import { useToast } from "../../../../contexts/ToastContext"
import { CHARGE_CARD_MUTATION, DEFAULT_PAYMENT_METHOD_QUERY } from "../../../../utils/mutations"
import { formatPrice, formatShortDate } from "../../../../utils/utils"
import Badge from "../../../shared/Badge"
import { Button, IconButton } from "../../../shared/Buttons"
import ConfirmManualChargeModal from "../../../shared/ConfirmManualChargeModal"
import { InputWithLabel } from "../../../shared/Inputs"
import LoadingSpinner from "../../../shared/LoadingSpinner"
import { NewModal } from "../../../shared/Modal"
import { RadioWithLabel } from "../../../shared/RadioButtons"
import Typography from "../../../shared/Typography"
import AddOrRetryButton from "../AddOrRetryButton"
import { MARK_AS_PAID, UNDO_MARK_PAID, RETRY_PAYMENT_MUTATION, REFRESH_PAYMENT_MUTATION } from "../appointment_graphql"

export default function AppointmentStatus() {
  const { appointment, setAppointment } = useManualBooking()

  const [, markPaid] = useMutation(MARK_AS_PAID)
  const [, undoMarkPaid] = useMutation(UNDO_MARK_PAID)
  const [, retryPayment] = useMutation(RETRY_PAYMENT_MUTATION)
  const [, refreshPayment] = useMutation(REFRESH_PAYMENT_MUTATION)
  const [{ fetching: chargingCard }, chargeCard] = useMutation(CHARGE_CARD_MUTATION)

  const [selectedPaymentOption, setSelectedPaymentOption] = useState("cash")
  const [otherPaymentOption, setOtherPaymentOption] = useState("")

  const options = [
    { label: "Cash", value: "cash" },
    { label: "Venmo", value: "venmo" },
    { label: "PayPal", value: "paypal" },
    { label: "Other", value: "other" }
  ]

  const handleChange = (event) => {
    setSelectedPaymentOption(event.target.value)
  }

  const [appointmentCopy, setAppointmentCopy] = useState(appointment)
  const [retryingPayment, setRetryingPayment] = useState(false)
  const [refreshingPayment, setRefreshingPayment] = useState(false)
  const [chargingCardNow, setChargingCardNow] = useState(false)
  const [showChargeConfirm, setShowChargeConfirm] = useState(false)
  const { showToast } = useToast()
  const { practice } = usePractice()

  const markAsPaid = (id) => {
    markPaid({
      appointmentId: id,
      paidBy: selectedPaymentOption === "other" ? otherPaymentOption : selectedPaymentOption
    }).then((res) => {
      NiceModal.hide("markPaidModal")
      if (res.data.markPaid.result === "success") {
        setAppointmentCopy({ ...appointment, markPaid: true })
      }
    })
  }

  const undoMarkAsPaid = (id) => {
    undoMarkPaid({ appointmentId: id }).then((res) => {
      if (res.data.undoMarkPaid.result === "success") {
        setAppointmentCopy({ ...appointment, markPaid: false, paymentStatus: "Unpaid" })
      }
    })
  }

  const handleRetryPayment = () => {
    setRetryingPayment(true)
    return retryPayment({ appointmentId: appointment?.id })
      .then((res) => {
        const payload = res.data?.retryPayment
        const result = payload?.result

        if (result === "success") {
          showToast("Success! Payment processed.")
          setAppointmentCopy({
            ...appointment,
            paymentError: false,
            hasPaymentMethod: true,
            paymentStatus: PAYMENT_STATUS_OPTIONS.PAID
          })
          return
        }

        if (result === "scheduled") {
          showToast("Payment queued. Capture is scheduled — this appointment stays unpaid until the charge settles.")
          setAppointmentCopy({
            ...appointment,
            paymentError: false,
            hasPaymentMethod: true,
            paymentStatus: PAYMENT_STATUS_OPTIONS.UNPAID
          })
          return
        }

        showToast({
          content: `Payment failed: ${payload?.errors?.[0] || "Internal server error"}`,
          type: "error"
        })
        setAppointmentCopy({ ...appointment, paymentError: payload?.errors?.[0] })
      })
      .finally(() => {
        setRetryingPayment(false)
      })
  }

  const handleRefreshPayment = () => {
    setRefreshingPayment(true)
    refreshPayment({ appointmentId: appointment?.id }).then((res) => {
      if (res.data?.refreshPayment?.result === "success") {
        showToast("Success! Payment refreshed.")
        setAppointmentCopy({ ...appointment, paymentError: false, hasPaymentMethod: true, paymentStatus: "Paid" })
      } else {
        showToast({
          content: `Payment refresh failed: ${res.data?.refreshPayment?.errors[0] || "Internal server error"}`,
          type: "error"
        })
        setAppointmentCopy({ ...appointment, paymentError: res.data?.refreshPayment?.errors[0] })
      }
      setRefreshingPayment(false)
    })
  }

  const executeChargeCardNow = () => {
    if (chargingCardNow || chargingCard) return
    setChargingCardNow(true)
    chargeCard({ appointmentId: appointment.id }).then((res) => {
      setChargingCardNow(false)
      setShowChargeConfirm(false)
      if (res.data?.chargeCard?.result === "success") {
        showToast({ content: "Payment processed successfully.", type: "success" })
        const paidAppointment = {
          ...appointmentCopy,
          paymentStatus: PAYMENT_STATUS_OPTIONS.PAID,
          paymentError: false,
          markPaid: false
        }
        setAppointmentCopy(paidAppointment)
        setAppointment(paidAppointment)
      } else {
        const message =
          res.data?.chargeCard?.errors?.join(", ") ||
          res.error?.message?.replace("[GraphQL]", "") ||
          "Payment could not be processed. Please try again."
        showToast({ content: message, type: "error" })
      }
    })
  }

  const [{ data, fetching, error }] = useQuery({
    query: DEFAULT_PAYMENT_METHOD_QUERY,
    variables: {
      clientId: appointment?.client?.id,
      userId: appointment?.user?.id,
      practiceId: practice.id
    },
    pause: !practice.hasPaymentProvider,
    requestPolicy: "cache-and-network"
  })

  useEffect(() => {
    // Only update hasPaymentMethod if practice has a payment provider
    // If practice doesn't have a payment provider, payment methods can't be used
    if (practice.hasPaymentProvider && !appointmentCopy.hasPaymentMethod && data?.defaultPaymentMethodPractice) {
      setAppointmentCopy({
        ...appointmentCopy,
        hasPaymentMethod: true,
        client: { ...appointmentCopy.client, defaultPaymentMethod: data.defaultPaymentMethodPractice }
      })
    }
  }, [data, appointmentCopy.hasPaymentMethod, practice.hasPaymentProvider])

  const upcoming = new Date(appointment.startsAt) >= new Date()
  // If practice doesn't have a payment provider, payment methods can't be used
  const effectiveHasPaymentMethod = practice.hasPaymentProvider && appointmentCopy.hasPaymentMethod
  const canShowWarning =
    !appointmentCopy.markPaid && appointmentCopy.amountCents > 0 && appointmentCopy.paymentStatus !== "Paid"
  const missingPaymentMethod = !appointmentCopy.paymentError && !effectiveHasPaymentMethod
  const overduePayment = canShowWarning && missingPaymentMethod && !upcoming
  const unprocessedPayment =
    canShowWarning &&
    !upcoming &&
    effectiveHasPaymentMethod &&
    appointmentCopy.paymentStatus === PAYMENT_STATUS_OPTIONS.UNPAID
  const failedPayment = canShowWarning && appointmentCopy.paymentError
  const recoveryQueued =
    canShowWarning && !upcoming && ["scheduled", "running"].includes(appointmentCopy.paymentCaptureStatus)
  // Manual-charge-only practices capture explicitly; do not surface auto-recovery / retry-card prompts.
  const manualChargeOverridesAutoCapture =
    practice.manualChargeEnabled && practice.requireClientCcToBook && practice.manualChargeOnly
  const recoveryQueuedForUi = recoveryQueued && !manualChargeOverridesAutoCapture
  const unprocessedPaymentForUi = unprocessedPayment && !manualChargeOverridesAutoCapture
  const needsPaymentAttention = overduePayment || failedPayment || unprocessedPaymentForUi || recoveryQueuedForUi
  let paymentActionTitle = "Retry card or send invoice"
  if (!practice.hasPaymentProvider) {
    paymentActionTitle = "Connect payment provider or mark paid"
  } else if (recoveryQueuedForUi) {
    paymentActionTitle = "Automatic recovery queued"
  } else if (overduePayment) {
    paymentActionTitle = "Add payment method or send invoice"
  }
  const addOrRetryLabel = overduePayment ? "Add payment method" : "Retry card"
  const client = appointmentCopy.user || appointmentCopy.client
  const isPackage = !!appointmentCopy.packageSessionNumber

  // Condition to allow practitioner to re-fetch payment details
  // Only enabled for Square for now
  const canRefreshPayment =
    appointmentCopy.state === "approved" &&
    appointmentCopy.hasPaymentMethod &&
    appointmentCopy.paymentMethod === "square"

  const cardOnFile = data?.defaultPaymentMethodPractice
  const canChargeCardNow =
    manualChargeOverridesAutoCapture && practice.hasPaymentProvider && effectiveHasPaymentMethod && canShowWarning
  const chargeAmountLabel = formatPrice(appointmentCopy.amountCents, practice.defaultCurrency || "USD")
  const clientDisplayName = client?.firstName || client?.name || "client"
  const chargeSummary = appointmentCopy.service?.name
    ? `${appointmentCopy.service.name} · ${formatShortDate(appointmentCopy.startsAt)}`
    : `appointment · ${formatShortDate(appointmentCopy.startsAt)}`

  if (fetching) return <LoadingSpinner />
  if (error)
    return (
      <Typography variant="body" className="mb-4 text-red">
        Error fetching payment method, please try again later.
      </Typography>
    )

  return (
    <div className="relative flex flex-col">
      <div className="mb-2 flex items-center justify-between">
        {!upcoming && needsPaymentAttention ? (
          <div className="inline-flex items-center gap-2">
            <Badge type="error">Unpaid</Badge>
            {canRefreshPayment && (
              <IconButton
                disabled={refreshingPayment}
                Icon={ArrowPathIcon}
                onClick={() => {
                  handleRefreshPayment()
                }}
                tooltip="Refresh payment status"
              />
            )}
          </div>
        ) : (
          (appointmentCopy.paymentStatus === "Paid" || appointmentCopy.markPaid) && <Badge>Paid</Badge>
        )}
        {appointmentCopy.amountCents > 0 && (appointmentCopy.paymentStatus !== "Paid" || appointmentCopy.markPaid) && (
          <>
            {appointmentCopy.markPaid ? (
              <div>
                <span className="text-sm text-teal">Marked as Paid</span>
                <Button
                  type="link"
                  className="ml-2 text-sm text-gray-dark"
                  onClick={() => {
                    undoMarkAsPaid(appointment.id)
                  }}>
                  Undo
                </Button>
              </div>
            ) : (
              <Button
                type="link"
                onClick={() => {
                  NiceModal.show("markPaidModal")
                }}>
                Mark as Paid
              </Button>
            )}
          </>
        )}
      </div>
      <Typography variant="small" className="mb-4 text-gray-dark">
        Booked by {appointment.bookedBy === "client" ? client.firstName : "me"} on{" "}
        {formatShortDate(appointment.createdAt)}
      </Typography>
      {canChargeCardNow && (
        <div className="mb-4 flex flex-col gap-3 rounded-xl border border-gray p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <Typography variant="body" className="mb-1 font-bold text-gray-dark">
              Card on file
            </Typography>
            {cardOnFile ? (
              <Typography variant="small" className="mb-0 text-gray-dark">
                {cardOnFile.brand} •••• {cardOnFile.last4}
                {cardOnFile.expMonth && cardOnFile.expYear ? ` · Exp ${cardOnFile.expMonth}/${cardOnFile.expYear}` : ""}
              </Typography>
            ) : (
              <Typography variant="small" className="mb-0 text-gray-dark">
                Payment method saved for this client
              </Typography>
            )}
          </div>
          <Button
            type="primary"
            size="small"
            className="shrink-0"
            disabled={chargingCardNow || chargingCard}
            onClick={() => setShowChargeConfirm(true)}>
            Charge card now
          </Button>
        </div>
      )}
      <ConfirmManualChargeModal
        visible={showChargeConfirm}
        hideModal={() => setShowChargeConfirm(false)}
        onConfirm={executeChargeCardNow}
        clientName={clientDisplayName}
        amountLabel={chargeAmountLabel}
        chargeSummary={chargeSummary}
        cardBrand={cardOnFile?.brand}
        cardLast4={cardOnFile?.last4}
        confirmLoading={chargingCardNow || chargingCard}
      />
      {needsPaymentAttention && (
        <>
          <div className="mb-4 rounded-xl border border-orange bg-orange-light p-4">
            {retryingPayment && (
              <Typography variant="body" className="mb-0">
                Processing payment. Please wait...
              </Typography>
            )}

            {!retryingPayment && (
              <Typography variant="body">
                <div className="font-bold">{paymentActionTitle}</div>
                {practice.hasPaymentProvider ? (
                  recoveryQueuedForUi ? (
                    <div>Automatic recovery is queued for this {isPackage ? "package" : "appointment"}.</div>
                  ) : (
                    <>
                      {overduePayment
                        ? "Add a payment method for this client or send an invoice."
                        : "Retry the saved card or send an invoice."}{" "}
                      <AddOrRetryButton
                        appointment={appointmentCopy}
                        headerLabel={"Choose your payment method"}
                        label={addOrRetryLabel}
                        onSave={handleRetryPayment}
                        type={"link"}
                      />
                    </>
                  )
                ) : (
                  <>
                    <div>
                      Connect a payment provider or mark this appointment paid if payment was collected elsewhere.
                    </div>
                    <a className="font-bold text-teal hover:underline" href="/portal/settings/banking">
                      Click here to connect to a payment provider
                    </a>
                  </>
                )}
              </Typography>
            )}
          </div>
        </>
      )}
      <NewModal
        id="markPaidModal"
        header="Mark as paid"
        showFooter={true}
        fullHeightInMobile={true}
        hideModal={() => {
          NiceModal.hide("markPaidModal")
        }}
        onSave={() => markAsPaid(appointment.id)}>
        <div className="sm:pb-12">
          <Typography variant="body" className="mb-4 text-gray-dark">
            Marking paid records an offline payment in Heal.me. It does not charge the client&apos;s card through Stripe
            or Square.
          </Typography>
          <Typography variant="body" className="mb-6 text-gray-dark">
            You can note how an offline payment was made:
          </Typography>
          {options.map((option) => (
            <RadioWithLabel
              key={option.value}
              id={option.value}
              label={option.label}
              checked={selectedPaymentOption === option.value}
              onChange={handleChange}
              value={option.value}
              name="paymentMethod"
              className="mb-4"
            />
          ))}
          {selectedPaymentOption === "other" && (
            <InputWithLabel
              label="Note how the client paid:"
              value={otherPaymentOption}
              onChange={(e) => setOtherPaymentOption(e.target.value)}
            />
          )}
        </div>
      </NewModal>
    </div>
  )
}
